import React, { useState } from 'react';
import gql from 'graphql-tag';
import sortBy from 'lodash/sortBy';
import includes from 'lodash/includes';
import Attachment from 'types/Attachment';
import Publisher from 'types/Publisher';
import RelayConnection from 'types/RelayConnection';
import { useBoolean, useIntl } from 'hooks';
import { MiniButton, Modal, Form, ErrorDebug, Button } from 'components';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { mapNodes, flashMessage } from 'helpers';
import * as ADD_MEDIA_APPEARANCES from 'graphql/mutations/addMediaAppearances.graphql';

interface Props {
  attachment: Attachment;
}

const PUBLISHERS = gql`
  query PublisherFilter {
    publishers {
      edges {
        node {
          id
          name
          image
        }
      }
    }
  }
`;

interface QueryData {
  publishers: RelayConnection<Publisher>;
}

export default function AddMediaAppearance(props: Props) {
  const { id, mediaAppearances } = props.attachment;
  const [isModalOpen, openModal, closeModal] = useBoolean();
  const [value, setValue] = useState<number[]>([]);
  const { t } = useIntl();

  const { data, loading, error } = useQuery<QueryData>(PUBLISHERS);

  const [addMediaAppearances, { loading: isSaving }] = useMutation<any>(
    ADD_MEDIA_APPEARANCES,
    {
      variables: { attachmentId: id, publisherIds: value },
      onError: () => {
        flashMessage('Global__UnexpectedError', { style: 'error' });
      },
      onCompleted: (data) => {
        if (data.addMediaAppearances && !data.addMediaAppearances.errors) {
          closeModal();
        } else {
          flashMessage('Global__UnexpectedError', { style: 'error' });
        }
      },
    }
  );

  const existingPublisherIds = mediaAppearances.map((ma) => ma.publisher.id);

  const options =
    data && data.publishers && data.publishers.edges
      ? sortBy(
          mapNodes(data.publishers.edges, (publisher) => ({
            value: publisher.id,
            label: publisher.name,
            image: publisher.image,
          })),
          'label'
        ).filter((opt) => !includes(existingPublisherIds, opt.value))
      : [];

  const placeholder = loading
    ? t('AddMediaAppearance__Loading')
    : t('AddMediaAppearance__Placeholder');

  return (
    <>
      <MiniButton onClick={openModal}>
        {t('AddMediaAppearance__OpenModal')}
      </MiniButton>

      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        contentLabel={t('AddMediaAppearance__Heading')}
        renderHeading={t('AddMediaAppearance__Heading')}
      >
        {error && <ErrorDebug error={error} />}

        <Form.Field>
          <Form.Select
            isMulti
            isLoading={loading}
            options={options}
            placeholder={placeholder}
            value={value}
            onChange={(ids) => setValue(ids)}
          />
        </Form.Field>

        <Modal.Actions>
          <Button cancel inlineBlock onClick={closeModal}>
            {t('Global__Cancel')}
          </Button>

          <Button
            filledBg
            inlineBlock
            onClick={addMediaAppearances}
            disabled={isSaving}
          >
            {t('Global__Save')}
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
}
